/**
 * /checkout/success?session_id=cs_xxx
 *
 * Polls /api/programs/checkout/status/{session_id} until payment_status === "paid",
 * then renders a success state with the auto-created enrollment id.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 30; // ≈ 60s

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const kind = params.get("kind"); // "pharmacy" or null

  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session_id in URL");
      return;
    }
    stopped.current = false;
    let cancelled = false;
    const tick = async (attempt) => {
      if (cancelled) return;
      try {
        const r = await api.get(`/programs/checkout/status/${sessionId}`);
        setStatus(r.data);
        if (r.data.payment_status === "paid" || r.data.status === "expired") {
          stopped.current = true;
          return;
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to fetch checkout status");
        stopped.current = true;
        return;
      }
      if (attempt + 1 < MAX_ATTEMPTS) {
        setTimeout(() => {
          setAttempts(attempt + 1);
          tick(attempt + 1);
        }, POLL_MS);
      } else {
        stopped.current = true;
      }
    };
    tick(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const paid = status?.payment_status === "paid";
  const expired = status?.status === "expired";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6 py-12">
      <div
        data-testid="checkout-success-page"
        className="max-w-lg w-full rounded-2xl border border-slate-800 bg-slate-900/40 p-8 text-center"
      >
        {error ? (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
            <div className="text-xl font-bold text-rose-200 mb-2">Something went wrong</div>
            <p className="text-sm text-slate-400">{error}</p>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <div className="text-2xl font-bold text-slate-100 mb-2">You're enrolled.</div>
            <p className="text-sm text-slate-400 max-w-md mx-auto mb-4">
              Payment confirmed. {kind === "pharmacy" ? "Your pharmacy will ship your order shortly." : "Your clinic will reach out next to coordinate your program."}
            </p>
            {status?.amount_cents != null && (
              <div className="text-xs text-slate-500 mb-2">
                Charged ${((status.amount_cents || 0) / 100).toFixed(2)} {String(status.currency || "USD").toUpperCase()}
              </div>
            )}
            {status?.enrollment_id && (
              <div
                data-testid="checkout-success-enrollment-id"
                className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mt-2"
              >
                Enrollment: {status.enrollment_id}
              </div>
            )}
            <div className="mt-6">
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-sm font-semibold px-4 py-2 hover:bg-blue-700"
              >
                Back to Nodera Health
              </Link>
            </div>
          </>
        ) : expired ? (
          <>
            <XCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <div className="text-xl font-bold text-amber-200 mb-2">Checkout session expired</div>
            <p className="text-sm text-slate-400">
              The checkout session ended without a payment. Please go back and try again.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-3" />
            <div className="text-xl font-bold text-slate-100 mb-2">Confirming your payment…</div>
            <p className="text-sm text-slate-400">
              This usually takes a few seconds. Don't close this page.
            </p>
            <div
              data-testid="checkout-success-attempts"
              className="text-[10px] text-slate-600 mt-3 uppercase tracking-wider"
            >
              attempt {attempts + 1} / {MAX_ATTEMPTS}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
